import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_NAMES, type UiLanguage } from '@b4b/shared';
import {
  useClusterDetail,
  useArticleContent,
  useSetRead,
  useSetSaved,
  useSettings,
} from '../api/queries.js';
import { useRelativeTime } from '../lib/time.js';
import { domainOf } from '../lib/url.js';
import { displaySummary, displayTitle } from '../lib/cardText.js';
import { usePremium } from '../lib/usePremium.js';
import { useArticleTranslation } from '../lib/useArticleTranslation.js';
import TranslatedBadge from '../components/TranslatedBadge.js';


/** Native name of the original language, the bare code when we don't know it. */
function sourceName(code: string | null): string {
  if (code && code in LANGUAGE_NAMES) return LANGUAGE_NAMES[code as UiLanguage];
  return (code ?? '?').toUpperCase();
}

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const relTime = useRelativeTime();
  const detail = useClusterDetail(id!);
  const settings = useSettings();
  const setRead = useSetRead();
  const setSaved = useSetSaved();
  const premium = usePremium();
  const [activeArticleId, setActiveArticleId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [heroFailed, setHeroFailed] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  const switched = useArticleContent(id!, activeArticleId);

  // representative or switched source
  const article = activeArticleId && switched.data ? switched.data.article : detail.data?.representative;
  const contentHtml = activeArticleId && switched.data ? switched.data.contentHtml : detail.data?.contentHtml;
  const aiCleanedBlocks =
    activeArticleId && switched.data ? switched.data.aiCleanedBlocks : detail.data?.aiCleanedBlocks;

  // the card translation carries title/summary only; the body is fetched on demand
  const {
    translation,
    translating,
    failed: translateFailed,
    retry: retryTranslation,
  } = useArticleTranslation(article, premium.active && !showOriginal && !!contentHtml);

  // auto-mark read on open (configurable)
  const markReadOnOpen = settings.data?.markReadOnOpen ?? true;
  useEffect(() => {
    if (detail.data && !detail.data.read && markReadOnOpen) {
      setRead.mutate({ id: detail.data.id, read: true });
    }
  }, [detail.data?.id, markReadOnOpen]);

  // avoid duplicating the hero image when content already starts with the same image
  const showHero = useMemo(() => {
    if (!article?.imageUrl || heroFailed) return false;
    if (!contentHtml) return true;
    const firstImg = contentHtml.match(/<img[^>]+src="([^"]+)"/);
    return !(firstImg && firstImg[1] === article.imageUrl);
  }, [article?.imageUrl, contentHtml, heroFailed]);

  if (detail.isLoading) {
    return <p className="py-12 text-center text-neutral-500">{t('common.loading')}</p>;
  }
  if (detail.isError || !detail.data || !article) {
    return (
      <p className="py-12 text-center text-neutral-500">
        {t('common.error')}{' '}
        <button onClick={() => navigate('/')} className="text-accent underline">
          {t('nav.feed')}
        </button>
      </p>
    );
  }

  const cluster = detail.data;
  const failed = article.contentStatus !== 'ok' || !contentHtml;
  const shownTitle = displayTitle(article, showOriginal);
  const shownSummary = displaySummary(article, showOriginal);
  const shownHtml = !showOriginal && translation?.contentHtml ? translation.contentHtml : contentHtml;

  const share = async () => {
    const shareData = { title: article.title, url: article.url };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // user cancelled
      }
    } else {
      await navigator.clipboard.writeText(article.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 pb-4 pt-20">
      <div className="mb-4 flex items-center gap-2">
        <button
          onClick={() => navigate(-1)}
          aria-label={t('common.close')}
          className="min-h-[44px] min-w-[44px] rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          ←
        </button>
        <div className="flex-1" />
        <button
          onClick={() => setSaved.mutate({ id: cluster.id, saved: !cluster.saved })}
          aria-label={cluster.saved ? t('article.unsave') : t('article.save')}
          className={`min-h-[44px] min-w-[44px] rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 ${cluster.saved ? 'text-amber-500' : ''}`}
        >
          {cluster.saved ? '★' : '☆'}
        </button>
        <button
          onClick={() => void share()}
          aria-label={t('article.share')}
          className="min-h-[44px] min-w-[44px] rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          {copied ? '✓' : '⤴'}
        </button>
        <a
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('article.openOriginal')}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg font-bold text-accent hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          ↗
        </a>
      </div>

      {showHero && article.imageUrl && (
        <div className="relative mb-5">
          <img
            src={article.imageUrl}
            alt=""
            referrerPolicy="no-referrer"
            onError={() => setHeroFailed(true)}
            className="max-h-96 w-full rounded-2xl object-cover"
          />
          {article.imageGenerated && (
            <span className="absolute left-3 top-3 rounded-full bg-black/50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white/85 backdrop-blur-sm">
              ✦ {t('feed.aiImage')}
            </span>
          )}
        </div>
      )}

      <h1 className="reader-title text-2xl leading-tight sm:text-3xl">{shownTitle}</h1>
      <p className="mt-2 text-sm text-neutral-500">
        <a
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-accent hover:underline"
        >
          {article.feedTitle ?? domainOf(article.url)}
        </a>
        {article.author ? ` · ${article.author}` : ''} · {relTime(article.publishedAt)}
        {article.readingTimeMin ? ` · ${t('feed.readingTime', { min: article.readingTimeMin })}` : ''}
      </p>

      {translation && (
        <p className="mt-2 flex flex-wrap items-center gap-x-2 text-sm text-neutral-500">
          <TranslatedBadge translation={translation} />
          <span>{t('translation.translatedFrom', { source: sourceName(translation.sourceLang) })}</span>
          <button onClick={() => setShowOriginal((v) => !v)} className="text-accent hover:underline">
            {showOriginal ? t('translation.showTranslation') : t('translation.showOriginal')}
          </button>
        </p>
      )}
      {translating && (
        <p className="mt-2 rounded-lg bg-neutral-100 px-3 py-2 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
          {t('translation.translating')}
        </p>
      )}
      {translateFailed && !showOriginal && (
        <p className="mt-2 flex items-center gap-2 rounded-lg bg-neutral-100 px-3 py-2 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
          {t('translation.failed')}
          <button onClick={retryTranslation} className="text-accent hover:underline">
            {t('common.retry')}
          </button>
        </p>
      )}

      {cluster.sources.length > 1 && (
        <p className="mt-3 text-sm text-neutral-500">
          {t('article.alsoOn')}{' '}
          {cluster.sources.map((source, i) => (
            <span key={source.articleId}>
              {i > 0 && ' · '}
              <button
                onClick={() =>
                  setActiveArticleId(
                    source.articleId === cluster.representative.id ? null : source.articleId,
                  )
                }
                className={`hover:underline ${
                  (activeArticleId ?? cluster.representative.id) === source.articleId
                    ? 'font-semibold text-accent'
                    : ''
                }`}
              >
                {source.feedTitle ?? domainOf(source.url)}
              </button>
            </span>
          ))}
        </p>
      )}

      <hr className="my-5 border-neutral-200 dark:border-neutral-800" />

      {failed ? (
        <div className="space-y-4">
          {shownSummary && <p className="reader-content mx-auto">{shownSummary}</p>}
          <p className="text-sm text-neutral-500">{t('article.extractionFailed')}</p>
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block rounded-xl bg-accent px-5 py-3 font-semibold text-white hover:bg-accent-strong"
          >
            {t('article.readOriginal', { source: article.feedTitle ?? domainOf(article.url) })}
          </a>
        </div>
      ) : (
        <>
          <div
            className="reader-content mx-auto"
            // content is sanitized server-side (sanitize-html allow-list)
            dangerouslySetInnerHTML={{ __html: shownHtml! }}
          />
          {aiCleanedBlocks != null && aiCleanedBlocks > 0 && (
            // rule-based removal (the key name "aiCleaned" is historical) — wording must not claim AI
            <p className="mt-6 text-center text-xs text-neutral-500 dark:text-neutral-400">
              ✦ {t('article.aiCleaned', { count: aiCleanedBlocks })}
            </p>
          )}
          <div className="my-8 text-center">
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-xl border border-accent px-5 py-3 font-semibold text-accent hover:bg-accent hover:text-white"
            >
              {t('article.readOriginal', { source: article.feedTitle ?? domainOf(article.url) })}
            </a>
          </div>
        </>
      )}
    </div>
  );
}
